import React, { useCallback, useEffect, useRef, useState } from "react";
import { ref, onValue, runTransaction, push, set } from "firebase/database";
import { db } from "../firebase";
import { logEvent } from "../analytics";

const REACTIONS: { key: string; emoji: string; label: string }[] = [
  { key: "sjungit", emoji: "🍻", label: "Sjungit den!" },
  { key: "rolig", emoji: "😂", label: "Rolig" },
  { key: "favorit", emoji: "❤️", label: "Favorit" },
];

const lsKey = (songKey: string, type: string) => `r_${songKey}_${type}`;

/** One-click reactions on a song. Counts are public, one vote per device. */
export const ReactionBar: React.FC<{ songKey: string }> = ({ songKey }) => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [mine, setMine] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try {
      const m: Record<string, boolean> = {};
      REACTIONS.forEach((r) => {
        if (localStorage.getItem(lsKey(songKey, r.key))) m[r.key] = true;
      });
      setMine(m);
    } catch (e) {
      /* ignore */
    }
    const unsub = onValue(ref(db, `reactions/${songKey}`), (snap) => {
      setCounts(snap.val() || {});
    });
    return () => unsub();
  }, [songKey]);

  const react = (type: string) => {
    if (mine[type]) return;
    setMine((m) => ({ ...m, [type]: true }));
    try {
      localStorage.setItem(lsKey(songKey, type), "1");
    } catch (e) {
      /* ignore */
    }
    logEvent("Reaction", type, songKey);
    runTransaction(ref(db, `reactions/${songKey}/${type}`), (cur) =>
      (typeof cur === "number" ? cur : 0) + 1
    ).catch(() => {});
  };

  return (
    <div className="sg-react">
      {REACTIONS.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => react(r.key)}
          className={mine[r.key] ? "sd-btn" : "sd-btn-out"}
          aria-pressed={!!mine[r.key]}
          aria-label={r.label}
        >
          <span style={{ marginRight: 6 }} aria-hidden="true">
            {r.emoji}
          </span>
          {r.label}
          {counts[r.key] ? <span className="sd-cnt">{counts[r.key]}</span> : null}
        </button>
      ))}
    </div>
  );
};

/** Lightweight feedback / song-request / correction prompt. */
export const FeedbackButton: React.FC<{
  context?: string;
  type?: string;
  label?: string;
  title?: string;
  placeholder?: string;
}> = ({
  context,
  type = "feedback",
  label = "Saknas en visa?",
  title = "Vad vill du säga?",
  placeholder = "Föreslå en visa, rapportera fel i texten eller säg vad du tycker.",
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [sent, setSent] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    if (openerRef.current) openerRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    if (areaRef.current) areaRef.current.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (!sent) return;
    const t = window.setTimeout(() => setSent(false), 4000);
    return () => window.clearTimeout(t);
  }, [sent]);

  const submit = () => {
    const text = message.trim();
    if (!text) return;
    const payload = {
      type,
      message: text.slice(0, 1000),
      contact: contact.trim().slice(0, 120) || "",
      context: context || "",
      path: typeof window !== "undefined" ? window.location.pathname : "",
      createdAt: Date.now(),
    };
    fetch(FEEDBACK_HOOK, { method: "POST", mode: "no-cors", headers: { "Content-Type": "text/plain" }, body: JSON.stringify(payload) })
      .catch(() => set(push(ref(db, "feedback")), payload).catch(() => {}));
    logEvent("Feedback", type, context || "");
    setOpen(false);
    setMessage("");
    setContact("");
    setSent(true);
  };

  const titleId = `fb-t-${type}-${context || "x"}`.replace(/[^\w-]/g, "-");

  return (
    <>
      <button
        type="button"
        ref={openerRef}
        className="sd-btn-text"
        onClick={() => setOpen(true)}
      >
        {label}
      </button>
      {open && (
        <div
          className="sd-dlg-bd"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="sd-dlg" role="dialog" aria-modal="true" aria-labelledby={titleId}>
            <h2 id={titleId}>{title}</h2>
            <div className="sd-dlg-body">
              <div className="sd-field">
                <textarea
                  ref={areaRef}
                  aria-label={title}
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={placeholder}
                  maxLength={1000}
                />
              </div>
              <div className="sd-field" style={{ marginBottom: 0 }}>
                <input
                  type="email"
                  aria-label="E-post (frivilligt, om du vill ha svar)"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="E-post (frivilligt, om du vill ha svar)"
                  maxLength={120}
                />
              </div>
              <span className="sd-dlg-note">Inget konto behövs. Vi läser allt som kommer in.</span>
            </div>
            <div className="sd-dlg-act">
              <button type="button" className="sd-btn-text" onClick={close}>
                Avbryt
              </button>
              <button type="button" className="sd-btn" onClick={submit} disabled={!message.trim()}>
                Skicka
              </button>
            </div>
          </div>
        </div>
      )}
      {sent && (
        <div className="sd-status" role="status">
          Tack! Din feedback är skickad.
        </div>
      )}
    </>
  );
};

const FEEDBACK_HOOK = "https://snapsdags-feedback.pion.dev/hooks/feedback";

export default FeedbackButton;
